import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface TencentHunyuan3dParams {
  /**
   * 文本提示词，与图像输入二选一，不超过1024 UTF-8字符
   * @length 0 - 1024
   */
  prompt?: string;
  /**
   * Base64编码的图像，与提示词二选一。支持格式: jpg, png, jpeg, webp，大小不超过8MB，分辨率128-5000px
   */
  image_base64?: string;
  /**
   * 图像URL，与提示词二选一。支持格式: jpg, png, jpeg, webp，大小不超过8MB，分辨率128-5000px
   */
  image_url?: string;
  /**
   * 输出3D模型格式，可选参数
   * @values OBJ | GLB | STL | USDZ | FBX | MP4
   */
  result_format?: 'OBJ' | 'GLB' | 'STL' | 'USDZ' | 'FBX' | 'MP4';
  /**
   * 是否启用PBR材质，可选参数
   */
  enable_pbr?: boolean;
  /**
   * 多视角图像数组，可选参数
   */
  multi_view_images?: Array<{
    /**
     * 视角
     * @values left | right | back
     */
    View?: 'left' | 'right' | 'back';
    /**
     * 该视角的Base64编码图像
     */
    ImageBase64?: string;
    /**
     * 该视角的图像URL
     */
    ImageUrl?: string;
  }>;
}

export interface TencentHunyuan3dResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * tencent_hunyuan_3d
 *
 * 腾讯混元3D生成接口
 * 
 * Model: Hunyuan 3D (固定模型)
 * Features: 文本生成3D模型、图像生成3D模型功能
 * Output Formats: OBJ、GLB、STL、USDZ、FBX、MP4（支持多种3D格式）
 * Advanced: 支持PBR材质渲染、多视角图像输入（左、右、后视角）
 * 
 * API Documentation: https://cloud.tencent.com/document/product/1804/120826
 * Status Query: https://cloud.tencent.com/document/product/1804/120827
 *
 * @example
 * ```typescript
 * import { initSeacloud, tencentHunyuan3d } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await tencentHunyuan3d({
 *   // 参数见 TencentHunyuan3dParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 TencentHunyuan3dParams 接口定义
 * @returns 生成的资源列表
 */
export async function tencentHunyuan3d(
  params: TencentHunyuan3dParams,
): Promise<TencentHunyuan3dResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 4000,
    maxAttempts: 90,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'tencent_hunyuan_3d',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: TencentHunyuan3dResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
